import type { SkillBundleId } from "./discovery.ts";

/**
 * The App's built-in Actions (ADR 0142). An Action is a Skill Bundle that
 * declares itself one: the bundle named by `declaredBy` carries
 * `afar-action: <id>` in its SKILL.md frontmatter, and a test holds this
 * catalog and those declarations together.
 *
 * What an Action does with the user's words lives in `@afar-research/afar`;
 * this catalog only says what the Action is called, which Skill Bundles it
 * invokes, and what a thread must offer for it to run.
 */

/** How picking an Action proceeds. */
export type ActionRun =
  /** A chip in the composer; the user writes the message and sends. */
  | "chip"
  /** Sends at once: there is nothing for the user to write. */
  | "immediate"
  /** Opens a small dialog first, which picks what the Action works on. */
  | "dialog";

/** What a thread must offer for an Action to run there. */
export type ActionRequirement =
  /** Design work changes canonical Design Systems, which live on the Primary Checkout. */
  | "primary-checkout"
  /** The peer council runs independent subagents. */
  | "native-subagents";

/** The icon a client draws for an Action. Clients map these names to their icon set. */
export type ActionIcon =
  | "books"
  | "hammer"
  | "flask"
  | "palette"
  | "file-text"
  | "users"
  | "photo"
  | "sparkles"
  | "trash"
  | "pencil";

export interface ActionVariant {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly skills: ReadonlyArray<SkillBundleId>;
}

export interface ActionDefinition {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly icon: ActionIcon;
  /** Verbs are the research pipeline; housekeeping tidies what the verbs made. */
  readonly kind: "verb" | "housekeeping";
  readonly run: ActionRun;
  /** The Skill Bundle whose frontmatter declares this Action. */
  readonly declaredBy: SkillBundleId;
  /** Skill Bundles invoked, in order, when the Action has no variants. */
  readonly skills: ReadonlyArray<SkillBundleId>;
  readonly variants?: ReadonlyArray<ActionVariant>;
  readonly requires?: ReadonlyArray<ActionRequirement>;
}

export const ACTIONS = [
  {
    id: "ingest",
    label: "Ingest",
    description: "Add sources to the Brain",
    icon: "books",
    kind: "verb",
    run: "chip",
    declaredBy: "ingest",
    skills: ["brain", "ingest"],
  },
  {
    id: "build",
    label: "Build",
    description: "Grill the plan, then implement it",
    icon: "hammer",
    kind: "verb",
    run: "chip",
    declaredBy: "implement",
    skills: ["grill-with-docs", "implement"],
  },
  {
    id: "experiment",
    label: "Experiment",
    description: "Run an investigation and record its evidence",
    icon: "flask",
    kind: "verb",
    run: "chip",
    declaredBy: "experiment",
    skills: ["experiment"],
  },
  {
    id: "design",
    label: "Design",
    description: "Create or change a Design System",
    icon: "palette",
    kind: "verb",
    run: "chip",
    declaredBy: "design",
    skills: ["design"],
    requires: ["primary-checkout"],
    variants: [
      {
        id: "create",
        label: "Create",
        description: "Start a new Design System",
        skills: ["design", "create-design"],
      },
      {
        id: "update",
        label: "Update",
        description: "Change an existing Design System",
        skills: ["design", "update-design"],
      },
      {
        id: "template",
        label: "Add template",
        description: "Add a reusable template",
        skills: ["design", "add-design-template"],
      },
      {
        id: "example",
        label: "Add example",
        description: "Add an example to learn from",
        skills: ["design", "add-design-example"],
      },
      {
        id: "sample",
        label: "Create sample",
        description: "Render a sample from the system",
        skills: ["design", "create-design-sample"],
      },
    ],
  },
  {
    id: "write",
    label: "Write",
    description: "Draft or revise the manuscript",
    icon: "file-text",
    kind: "verb",
    run: "chip",
    declaredBy: "manuscript",
    skills: ["manuscript"],
  },
  {
    id: "review",
    label: "Review",
    description: "Ask a council of reviewers",
    icon: "users",
    kind: "verb",
    run: "chip",
    declaredBy: "peer-council",
    skills: ["peer-council"],
    requires: ["native-subagents"],
  },
  {
    id: "media",
    label: "Media",
    description: "Make slides, a website, or images",
    icon: "photo",
    kind: "verb",
    run: "chip",
    declaredBy: "powerpoint",
    skills: ["powerpoint"],
    variants: [
      {
        id: "slides",
        label: "Slides",
        description: "Present a story, pitch, or idea",
        skills: ["powerpoint"],
      },
      {
        id: "website",
        label: "Website",
        description: "Build a website, mockup, or visualization",
        skills: ["website"],
      },
      {
        id: "images",
        label: "Images",
        description: "Create raster images or editable vectors",
        skills: ["images"],
      },
    ],
  },
  {
    id: "brain-lint",
    label: "Lint the Brain",
    description: "Find and repair problems across the whole Brain",
    icon: "sparkles",
    kind: "housekeeping",
    run: "immediate",
    declaredBy: "lint",
    skills: ["brain", "lint"],
  },
  {
    id: "brain-delete",
    label: "Delete Brain sources",
    description: "Remove captured sources and repair the pages that cited them",
    icon: "trash",
    kind: "housekeeping",
    run: "dialog",
    declaredBy: "delete",
    skills: ["brain", "delete"],
  },
  {
    id: "design-rename",
    label: "Rename design system",
    description: "Settle a new name for a Design System",
    icon: "pencil",
    kind: "housekeeping",
    run: "dialog",
    declaredBy: "rename-design-system",
    skills: ["design", "rename-design-system"],
    requires: ["primary-checkout"],
  },
  {
    id: "design-delete",
    label: "Delete design system",
    description: "Remove a Design System from the project",
    icon: "trash",
    kind: "housekeeping",
    run: "dialog",
    declaredBy: "design",
    skills: [],
    requires: ["primary-checkout"],
  },
] as const satisfies ReadonlyArray<ActionDefinition>;

export type ActionId = (typeof ACTIONS)[number]["id"];

const ACTIONS_BY_ID = new Map<string, ActionDefinition>(ACTIONS.map((action) => [action.id, action]));

export const findAction = (id: string): ActionDefinition | undefined => ACTIONS_BY_ID.get(id);

/** The Skill Bundles an Action invokes for one variant, or for the Action itself. */
export const actionSkills = (
  action: ActionDefinition,
  variantId?: string,
): ReadonlyArray<SkillBundleId> =>
  (variantId === undefined
    ? undefined
    : action.variants?.find((variant) => variant.id === variantId)?.skills) ?? action.skills;
